/**
 * Allys Projects List Skeleton
 *
 * Matches AllysProjectsListView: page header with gradient line,
 * sticky toolbar with search/filters, and vertical project card stack.
 * Includes navigation skeleton so the nav bar is always visible.
 */

import { AllysNavSkeleton } from './AllysNavSkeleton';

export function AllysProjectsSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <AllysNavSkeleton />

      {/* Page header */}
      <div className="relative bg-white border-b border-gray-100 skeleton-stagger-1">
        <div className="absolute top-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-amber-400/60 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="space-y-3">
              <div className="h-3 w-40 rounded bg-gray-200 skeleton-shimmer" />
              <div className="h-9 w-64 rounded-lg bg-gray-200 skeleton-shimmer" />
              <div className="h-4 w-80 max-w-full rounded bg-gray-100 skeleton-shimmer" />
            </div>
            <div className="flex gap-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-8 w-24 rounded-full bg-gray-100 skeleton-shimmer" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky toolbar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm skeleton-stagger-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3">
            <div className="h-10 flex-1 max-w-md rounded-xl bg-gray-100 skeleton-shimmer" />
            <div className="h-6 w-px bg-gray-200 hidden lg:block" />
            <div className="hidden lg:flex gap-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 w-20 rounded-full bg-gray-100 skeleton-shimmer" />
              ))}
            </div>
            <div className="h-8 w-28 rounded-lg bg-gray-100 ml-auto skeleton-shimmer" />
          </div>
        </div>
      </div>

      {/* Project cards */}
      <div className="bg-linear-to-b from-gray-50 via-gray-100 to-gray-50 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 lg:space-y-12">
          {[3, 4, 5].map((stagger) => (
            <div key={stagger} className={`bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 skeleton-stagger-${stagger}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="aspect-16/10 lg:aspect-auto bg-linear-to-br from-gray-200 to-gray-100 skeleton-shimmer" />
                <div className="p-8 space-y-4">
                  <div className="flex gap-2">
                    <div className="h-6 w-20 rounded-full bg-amber-100 skeleton-shimmer" />
                    <div className="h-6 w-16 rounded-full bg-gray-100 skeleton-shimmer" />
                  </div>
                  <div className="h-7 w-3/4 rounded-lg bg-gray-200 skeleton-shimmer" />
                  <div className="h-4 w-1/2 rounded bg-gray-100 skeleton-shimmer" />
                  <div className="space-y-2 pt-2">
                    <div className="h-3 w-full rounded bg-gray-100 skeleton-shimmer" />
                    <div className="h-3 w-5/6 rounded bg-gray-100 skeleton-shimmer" />
                  </div>
                  <div className="flex gap-4 pt-4">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="space-y-1">
                        <div className="h-5 w-10 rounded bg-gray-200 skeleton-shimmer" />
                        <div className="h-3 w-14 rounded bg-gray-100 skeleton-shimmer" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
